import classNames from 'classnames';
import ReactGA from 'react-ga4';
import { useHistory } from 'react-router-dom';

import { SamaritanMemberSmallTemplate } from '../../hooks/useAdminMemberSearch';

import { capitalizeWord } from 'util/index';

import { ButtonBase, CardMedia, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';

const useStyles = makeStyles(() =>
    createStyles({
        content: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            height: '100%'
        },
        cardContainer: {
            backgroundColor: 'rgb(137,157,193)',
            borderRadius: '10px',
            height: '100%',
            overflow: 'hidden',
            position: 'relative',
            width: '100%',
            '& img': {
                height: '100%',
                objectFit: 'cover',
                objectPosition: '50% 30%',
                width: '100%'
            }
        },
        holderName: {
            bottom: '12px',
            left: '16px',
            color: '#FFFFFF',
            fontSize: '20px',
            fontWeight: 700,
            lineHeight: '24px',
            position: 'absolute',
            wordBreak: 'break-word'
        },
        overlay: {
            position: 'absolute',
            backgroundColor: 'rgba(0,0,0,0.4)',
            height: '100%',
            width: '100%'
        },
        overlayDark: {
            backgroundColor: 'rgba(0,0,0,0.7)'
        },
        statusTag: {
            position: 'absolute',
            left: '0',
            top: '16px',
            borderRadius: '0 4px 4px 0',
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '2px',
            lineHeight: '14px',
            padding: '3px 12px',
            textTransform: 'uppercase',
            zIndex: 1
        },
        pending: {
            backgroundColor: '#FEF3E7',
            color: '#7A4406'
        },
        exited: {
            backgroundColor: '#FEE7EC',
            color: '#A80A2D'
        }
    })
);

interface AdminMemberCardProps {
    member: SamaritanMemberSmallTemplate;
}

const AdminMemberCard: React.FC<AdminMemberCardProps> = ({ member }) => {
    const classes = useStyles();
    const history = useHistory();

    const { mobile_photo, profile_stage } = member;

    const handleClick = () => {
        ReactGA.event({
            category: 'Admin',
            action: 'View Member',
            label: member.id.toString()
        });

        history.push(`/holders/${member.id}`);
    };

    const getStatusTagClass = () => {
        switch (profile_stage) {
            case 'ELIGIBILITY_LIST':
            case 'TARGET_LIST':
            case 'PENDING':
                return classes.pending;
            case 'EXITED':
            case 'NEVER_ENGAGED':
            case 'UNSUCCESSFUL_PENDING':
            case 'TARGET_NOT_INTERESTED':
            case 'NO_LONGER_ELIGIBLE':
                return classes.exited;
            default:
                return '';
        }
    };

    const getStatusText = () => {
        switch (profile_stage) {
            case 'PENDING':
                return 'pending';
            case 'ELIGIBILITY_LIST':
                return 'eligibility list';
            case 'TARGET_LIST':
                return 'target list';
            case 'UNSUCCESSFUL_PENDING':
                return 'pending membership ended';
            case 'EXITED':
                return 'exited';
            case 'NEVER_ENGAGED':
                return 'never engaged';
            case 'TARGET_NOT_INTERESTED':
                return 'not interested';
            case 'NO_LONGER_ELIGIBLE':
                return 'no longer eligible';
            default:
                return '';
        }
    };

    const shouldShowTag = [
        'ELIGIBILITY_LIST', 
        'TARGET_LIST', 
        'PENDING', 
        'EXITED', 
        'NEVER_ENGAGED', 
        'UNSUCCESSFUL_PENDING', 
        'TARGET_NOT_INTERESTED', 
        'NO_LONGER_ELIGIBLE'
    ].includes(profile_stage);
    const shouldShowDarkOverlay = shouldShowTag;

    const overlayClass = classNames(classes.overlay, {
        [classes.overlayDark]: shouldShowDarkOverlay
    });

    const statusTagClass = classNames(classes.statusTag, getStatusTagClass());

    return (
        <ButtonBase
            className={classes.content}
            component="div"
            onClick={handleClick}
        >
            <div className={classes.cardContainer}>
                {shouldShowTag && (
                    <div className={statusTagClass}>
                        {getStatusText()}
                    </div>
                )}
                <span className={overlayClass} />
                <CardMedia
                    component="img"
                    image={mobile_photo}
                    loading="lazy"
                />
                <Typography className={classes.holderName}>
                    {capitalizeWord(member.name)}
                </Typography>
            </div>
        </ButtonBase>
    );
};

export default AdminMemberCard; 